// Modules
import useSWR from "swr";
// API
import api from "@/helpers/api";
// Actions
import { getData } from "../getDataAction";
// Helpers
import { endSpinner, startSpinner } from "@/config/store/spinnerReducer";
import type { UserEvent } from "@/lib/types/types";
import { differenceInHours } from "date-fns";
import { SUBSTITUTES } from "@/helpers/constants_db";

export const EVENTS_APIs = (patientId: string) => `calendar/patient/${patientId}`;
export const SUBSTITUTE_APIs = (patientId: string) => `patient/${patientId}/substitutes`;

/*
 * Events APIs
 */
export const getPatientEventsSWR = (patientId: string) => {
    const fetcher = (url: any) =>
        getData<UserEvent[]>({
            displaySpinner: false,
            url
        });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, mutate, isValidating } = useSWR(patientId ? `${EVENTS_APIs(patientId)}/events` : null, fetcher, {
        fallbackData: [],
        revalidateOnFocus: false
    });

    const dataMapped = data?.map(entry => ({
        ...entry,
        start: new Date(entry.start),
        end: new Date(entry.end)
    }));
    return { events: dataMapped ?? [], refreshEvents: mutate, isLoading: isValidating };
};

export const createSessionEvent = async ({ userId, data, params, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .post({ path: `${EVENTS_APIs(userId)}/event/create`, data, params })
        .then(res => {
            successCb && successCb(res.data.eventId);
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error creating an event:", err);

            const errorMessage = `error-calendar-event-creation-${err.response?.status}`;

            errorCb && errorCb(errorMessage);
            dispatch(endSpinner());
        });
};

export const updateSessionEvent = async ({ userId, eventId, data, params, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .put({ path: `${EVENTS_APIs(userId)}/event/${eventId}/update`, data, params })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error updating an event:", err);

            const errorMessage = `error-calendar-event-update-${err.response?.status}`;

            errorCb && errorCb(errorMessage);
            dispatch(endSpinner());
        });
};

export const deleteSessionEvent = async ({ userId, eventId, params, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .delete({ path: `${EVENTS_APIs(userId)}/event/${eventId}/delete`, params })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error deleting an event:", err);
            const errorMessage = `error-calendar-event-delete-${err.response?.status}`;

            errorCb && errorCb(errorMessage);
            dispatch(endSpinner());
        });
};

/*
 * Substitute APIs
 */

export const PatientSubstitutesSWR = (patientId: string) => {
    const fetcher = (url: string) =>
        getData({
            fullScreenSpinner: true,
            url
        });

    // Fetch substitutes using SWR
    const { data, isValidating, mutate } = useSWR(patientId ? `${SUBSTITUTE_APIs(patientId)}/list` : null, fetcher, {
        fallbackData: [],
        revalidateOnFocus: false,
        refreshInterval: 1000 * 60 * 60
    });

    // Return whether there are any substitutes available
    const isSubstituteEligible = data?.postponed_at
        ? differenceInHours(new Date(), new Date(data.postponed_at)) >= 48 &&
          Array.isArray(data?.[SUBSTITUTES]) &&
          data[SUBSTITUTES].length > 0
        : Array.isArray(data?.[SUBSTITUTES]) && data[SUBSTITUTES].length > 0;

    return {
        substitutes: data?.[SUBSTITUTES] || [],
        isSubstituteEligible,
        hasSubstituteSessions: data?.[SUBSTITUTES]?.length > 0,
        refreshSubstitutes: mutate,
        isLoading: isValidating
    };
};

export const updateSubsituteAvailableSessions = async ({ patientId, substituteId, data, dispatch, successCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .put({ path: `${SUBSTITUTE_APIs(patientId)}/${substituteId}/update`, data })
        .then(() => {
            successCb && successCb();
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error updating substitute available sessions:", err);
            dispatch(endSpinner());
        });
};

export const deleteSubstitute = async ({ patientId, substituteId, data, dispatch, successCb, errorCb }) => {
    dispatch(startSpinner({ fullScreen: true }));

    return await api
        .delete({ path: `${SUBSTITUTE_APIs(patientId)}/${substituteId}/delete`, data })
        .then(res => {
            const availableSubsitutes = res?.data;

            successCb && successCb(availableSubsitutes);
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error deleting substitute:", err);
            errorCb && errorCb();

            dispatch(endSpinner());
        });
};

export const parentHasSubstitutes = async patientId => {
    return await api
        .get({ path: `${SUBSTITUTE_APIs(patientId)}/list` })
        .then(response => {
            const data = response?.data;
            return data;
        })
        .catch(err => {
            console.error("Error getting substitutes for the patient:", err);
        });
};

/*
 * Buffer APIs
 */

export const calculateTherapyBuffer = async ({ id, dispatch, errorCb, successCb }) => {
    dispatch(startSpinner());

    return await api
        .post({ path: `patient/${id}/therapy_buffer` })
        .then(response => {
            const data = response.data;
            successCb && successCb(data);
            dispatch(endSpinner());
        })
        .catch(err => {
            console.error("Error calculating therapy buffer:", err);

            errorCb && errorCb(err);
            dispatch(endSpinner());
        });
};
